import type {
  LeadFlowAnswerLabels,
  LeadFlowAnswers,
  LeadFlowChoiceFieldName,
} from "../types";

import {
  CARE_OPTION_LABELS,
  CARE_RECIPIENT_LABELS,
  getUrgencyLabel,
} from "@homeinstead/forms";
import { getSelectedCareTypes } from "./get-selected-care-types";

type LeadFlowDisplayedAnswerField = LeadFlowChoiceFieldName | "postalCode";

function getDefaultChoiceLabel(
  fieldName: LeadFlowChoiceFieldName,
  value: string,
): string {
  if (fieldName === "careFor") {
    return (
      CARE_RECIPIENT_LABELS[value as keyof typeof CARE_RECIPIENT_LABELS] ??
      value
    );
  }

  if (fieldName === "careType") {
    return (
      CARE_OPTION_LABELS[value as keyof typeof CARE_OPTION_LABELS] ?? value
    );
  }

  return getUrgencyLabel(value);
}

function getChoiceLabel(
  fieldName: LeadFlowChoiceFieldName,
  value: string,
  answerLabels: LeadFlowAnswerLabels,
): string {
  return (
    answerLabels[fieldName]?.[value] ?? getDefaultChoiceLabel(fieldName, value)
  );
}

/**
 * Formats one stored answer for display. Single-choice answers show the
 * flow's option label (or the contract default); care types show every
 * selected label in the order the flow offered them; the postal code shows as
 * typed. Returns undefined while the question is unanswered.
 */
function getAnswerText(
  fieldName: LeadFlowDisplayedAnswerField,
  answers: LeadFlowAnswers,
  answerLabels: LeadFlowAnswerLabels,
): string | undefined {
  if (fieldName === "postalCode") {
    return answers.postalCode || undefined;
  }

  if (fieldName === "careType") {
    const offeredValues = Object.keys(answerLabels.careType ?? {});
    const getOfferedIndex = (value: string) => {
      const index = offeredValues.indexOf(value);
      return index === -1 ? offeredValues.length : index;
    };
    const selectedValues = getSelectedCareTypes(answers).toSorted(
      (left, right) => getOfferedIndex(left) - getOfferedIndex(right),
    );

    return (
      selectedValues
        .map((value) => getChoiceLabel("careType", value, answerLabels))
        .join(", ") || undefined
    );
  }

  const value = answers[fieldName];

  return value ? getChoiceLabel(fieldName, value, answerLabels) : undefined;
}

export { getAnswerText };
